import Link from 'next/link';
import { ReactNode } from 'react';
import {
    IoArrowForward,
    IoCheckmark,
    IoEyeOutline,
    IoPeopleOutline,
    IoQrCodeOutline,
    IoQrCodeSharp,
} from 'react-icons/io5';

import { Anchor, Badge, Box, Group, SimpleGrid, Stack, Text, ThemeIcon } from '@mantine/core';

import { CompanyProfile, PublishedStatus } from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { EventHelpers } from 'helpers/EventHelpers';
import { IntlHelpers } from 'helpers/IntlHelpers';
import { QrScanHelpers } from 'helpers/QrScanHelpers';

import { BoothNumber } from 'components/booth-number/BoothNumber';
import { useEventReadiness } from 'components/company-profile/hooks/use-event-readiness';
import { DashboardPanel } from 'components/dashboard-panel/DashboardPanel';
import cxPanel from 'components/dashboard-panel/DashboardPanel.module.scss';
import { DashboardActionRow } from 'components/homepage/components/dashboard/DashboardActionRow';
import { IKImage } from 'components/ik-image/IKImage';
import { ReplusLogo } from 'components/logo/ReplusLogo';
import { useQrScansByCompany } from 'hooks/use-qr-scans-by-company';
import { DateService } from 'services/DateService';

import cx from './EventReadinessCard.module.scss';

const EventReadinessCard = ({
    company,
    compact,
    children,
}: {
    company: CompanyProfile;
    compact?: boolean;
    children?: ReactNode;
}) => {
    const { event, booth, eventIsHappeningNow, eventHasEnded, headline, steps, nextStep, doneCount } =
        useEventReadiness(company);
    const { qrScans } = useQrScansByCompany({ companyId: company.id });

    if (!event) {
        return null;
    }

    const showExplainer = !eventIsHappeningNow && Boolean(nextStep);

    const eventLeads = QrScanHelpers.groupByVisitor(qrScans.filter((scan) => scan.event === event.id)).length;
    const leadsDescription = eventLeads
        ? `${IntlHelpers.formatCount(eventLeads, 'lead', 'leads')} at ${event.name}`
        : 'Scans at your booth show up here';

    const eventDetails = compact ? (
        <Stack gap={2} miw={0}>
            <Text fz="xs" fw={600} c="violet">
                {eventIsHappeningNow
                    ? 'Happening now'
                    : eventHasEnded
                      ? 'Ended'
                      : `Starts ${DateService.formatDistanceToNow(event.start)}`}
            </Text>
            <Anchor component={Link} href={EventHelpers.urls.view(event.slug)} fw={700} c="inherit" lh={1.2}>
                {event.name}
            </Anchor>
            <Text size="sm" c="dimmed">
                {[EventHelpers.formatDate(event), booth && `Booth ${booth}`].filter(Boolean).join(' · ')}
            </Text>
        </Stack>
    ) : (
        <Stack gap={4} miw={0}>
            <Text fw={700} size="lg" lh={1.2}>
                {headline}
            </Text>
            <Group gap={6} wrap="nowrap">
                <Text size="sm" c="dimmed">
                    {[EventHelpers.formatDate(event), event.location].filter(Boolean).join(' · ')}
                </Text>
                <Text size="sm" c="dimmed">
                    ·
                </Text>
                <Anchor component={Link} href={EventHelpers.urls.view(event.slug)} fw={500} fz="sm">
                    View event
                </Anchor>
            </Group>
        </Stack>
    );

    return (
        <DashboardPanel
            shadedHeader
            live={eventIsHappeningNow}
            header={
                <Group gap="md" wrap="nowrap" align={compact ? 'flex-start' : 'center'} miw={0}>
                    <Box
                        w={compact ? 72 : 96}
                        h={compact ? 48 : 64}
                        style={{
                            flexShrink: 0,
                            overflow: 'hidden',
                            borderRadius: 'var(--mantine-radius-md)',
                            border: '1px solid var(--mantine-color-gray-2)',
                            background: event.background || 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {event.backgroundImage ? (
                            <IKImage
                                fileOrId={event.backgroundImage}
                                width={192}
                                height={128}
                                alt={event.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <ReplusLogo w={48} />
                        )}
                    </Box>
                    {eventDetails}
                </Group>
            }
            rightSection={!compact && <BoothNumber event={event} company={company} showEventName={false} />}
        >
            <Stack gap="sm">
                {showExplainer && (
                    <SimpleGrid cols={compact ? 1 : { base: 1, sm: 3 }} spacing="sm">
                        {[
                            { icon: <IoEyeOutline />, label: 'Attendees preview your booth' },
                            { icon: <IoQrCodeOutline />, label: 'QR codes for your booth' },
                            { icon: <IoPeopleOutline />, label: 'Every scan becomes a lead' },
                        ].map(({ icon, label }) => (
                            <Group key={label} gap="xs" wrap="nowrap">
                                <ThemeIcon variant="light" color="violet" size="md" radius="xl" fz={14}>
                                    {icon}
                                </ThemeIcon>
                                <Text size="sm" c="dimmed">
                                    {label}
                                </Text>
                            </Group>
                        ))}
                    </SimpleGrid>
                )}

                {!eventIsHappeningNow &&
                    (nextStep ? (
                        <Box className={cx.checklist}>
                            <Group justify="space-between" mb={6}>
                                <Text size="xs" fw={700} tt="uppercase" c="dimmed">
                                    Booth checklist
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {doneCount} of {steps.length} done
                                </Text>
                            </Group>
                            <Stack gap={6}>
                                {steps.map(({ label, viewLabel, done, href }) => (
                                    <Group key={label} gap="sm" wrap="nowrap" justify="space-between">
                                        <Group gap="sm" wrap="nowrap">
                                            <ThemeIcon
                                                size={22}
                                                radius="xl"
                                                variant={done ? 'filled' : 'outline'}
                                                color={done ? 'green' : 'gray.3'}
                                            >
                                                <IoCheckmark size={14} />
                                            </ThemeIcon>
                                            <Anchor
                                                component={Link}
                                                href={href}
                                                size="sm"
                                                fw={500}
                                                underline={done ? 'never' : 'hover'}
                                                c={done ? 'dimmed' : 'inherit'}
                                                td={done ? 'line-through' : undefined}
                                            >
                                                {label}
                                            </Anchor>
                                        </Group>
                                        <Anchor
                                            component={Link}
                                            href={href}
                                            size="sm"
                                            fw={600}
                                            c={done ? 'dimmed' : undefined}
                                            aria-label={done ? viewLabel : 'Do it'}
                                        >
                                            <Group gap={4} wrap="nowrap">
                                                {!compact && (done ? viewLabel : 'Do it')}
                                                <IoArrowForward size={14} />
                                            </Group>
                                        </Anchor>
                                    </Group>
                                ))}
                            </Stack>
                        </Box>
                    ) : (
                        <Group gap="sm" wrap="nowrap" className={cx.checklist}>
                            <ThemeIcon size={22} radius="xl" color="green">
                                <IoCheckmark size={14} />
                            </ThemeIcon>
                            <Text size="sm" c="green.8" fw={600}>
                                {company.status === PublishedStatus.PUBLISHED
                                    ? 'Booth checklist done. Print your QR codes and bring them to the booth.'
                                    : 'Booth checklist done. Publish your profile to get your QR codes.'}
                            </Text>
                        </Group>
                    ))}

                <SimpleGrid
                    cols={compact ? 1 : { base: 1, sm: 2 }}
                    spacing="xs"
                    verticalSpacing={0}
                    className={compact ? cxPanel.dividedList : cxPanel.dividedGrid}
                >
                    <DashboardActionRow
                        icon={<IoPeopleOutline />}
                        title="Leads"
                        description={leadsDescription}
                        href={CompanyHelpers.urls.showtimeLeads(company.slug)}
                        rightSection={
                            eventLeads > 0 && (
                                <Badge color="red" size="sm">
                                    {IntlHelpers.formatNumber(eventLeads)}
                                </Badge>
                            )
                        }
                    />
                    <DashboardActionRow
                        icon={<IoQrCodeSharp />}
                        title="Your QR codes"
                        description="Print them for your booth"
                        href={CompanyHelpers.urls.qr(company.slug)}
                    />
                </SimpleGrid>

                {children}
            </Stack>
        </DashboardPanel>
    );
};

export { EventReadinessCard };
